import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import JobSection from './components/JobSection';
import JobMonitoring from './components/JobMonitoring';
import Settings from './components/Settings';
import PushJobSection from './components/PushJobSection';
import Projects from './components/Projects/Projects';
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import { ApiService } from './services/api';

import CorrectionRecommendation from './components/CorrectionRecommendation';
import ProfilingPage from './components/ProfilingPage';

// Layout component that includes the sidebar
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { selectedProject, projectList, setProjectList, setSelectedProject } = useProject();

  // Fetch projects for the sidebar on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await ApiService.getProjects();
        setProjectList(response.data);
      } catch (error) {
        console.error('Error fetching projects for sidebar:', error);
      }
    };

    fetchProjects();
  }, [setProjectList]);

  // Keep selected project in sync with URL query param (?projectId=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get('projectId');
    if (!pid) return;
    const projectId = Number(pid);
    if (!Number.isFinite(projectId)) return;

    // If different from current selection, try to set from list; fallback to id-only
    if (selectedProject?.id !== projectId) {
      const match = projectList.find(p => p.id === projectId);
      if (match) {
        setSelectedProject(match);
      } else {
        // Fallback: at least set ApiService so requests are scoped until list arrives
        ApiService.setCurrentProjectId(projectId);
      }
    }
  }, [location.search, projectList, selectedProject?.id, setSelectedProject]);

  // Determine active section based on current path
  const getActiveSection = () => {
    const path = location.pathname;
    if (path === '/') return 'dashboard';
    if (path === '/metadata-management') return 'metadata-management';
    if (path === '/push-job') return 'push-job';
    if (path === '/job-monitoring') return 'job-monitoring';
    if (path === '/settings') return 'settings';
    if (path === '/projects') return 'projects';

    if (path.startsWith('/correction-recommendation/')) return 'job-monitoring';
    if (path.startsWith('/profiling-page/')) return 'job-monitoring';
    return 'dashboard';
  };

  const handleSectionChange = (section: string) => {
    // This will be handled by React Router navigation
    // We can keep this for compatibility but won't use it for navigation
  };

  const handleProjectSelect = (project: any) => {
    // Update the selected project in the context
    setSelectedProject(project);
    console.log('Project selected:', project.name);
  };

  return (
    <div className="flex h-screen bg-secondary-50">
      {/* Fixed Sidebar */}
      <div className="fixed left-0 top-0 h-full z-10">
        <Sidebar
          activeSection={getActiveSection()}
          onSectionChange={handleSectionChange}
          selectedProject={selectedProject}
          projectList={projectList}
          onProjectSelect={handleProjectSelect}
        />
      </div>

      {/* Main Content Area with dynamic offset from resizable sidebar and 10px page spacing */}
      <div
        className="flex-1 overflow-auto page-root"
        style={{
          // Ensure content starts 10px from the sidebar's right edge
          marginLeft: 'calc(var(--sidebar-width) + var(--app-gap))',
          // Apply 10px top, right, bottom spacing; left spacing is covered by marginLeft above
          paddingTop: 'var(--app-gap)',
          paddingRight: 'var(--app-gap)',
          paddingBottom: 'var(--app-gap)'
        }}
      >
        <div className="min-h-full">
          {children}
        </div>
      </div>
    </div>
  );
};

function RoutesWithProjectKey() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const projectKey = params.get('projectId') || 'none';

  return (
    <Routes>
      <Route path="/" element={
        <Layout>
          <Dashboard isActive={true} />
        </Layout>
      } />
      <Route path="/projects" element={
        <Layout>
          <Projects />
        </Layout>
      } />
      <Route path="/metadata-management" element={
        <Layout>
          <JobSection key={`metadata-${projectKey}`} isActive={true} />
        </Layout>
      } />
      <Route path="/push-job" element={
        <Layout>
          <PushJobSection key={`pushjob-${projectKey}`} isActive={true} />
        </Layout>
      } />
      <Route path="/job-monitoring" element={
        <Layout>
          <JobMonitoring key={`jobmon-${projectKey}`} isActive={true} />
        </Layout>
      } />
      <Route path="/settings" element={
        <Layout>
          <Settings />
        </Layout>
      } />

      <Route path="/correction-recommendation/:jobId" element={
        <CorrectionRecommendation />
      } />
      <Route path="/profiling-page/:jobId" element={
        <ProfilingPage isActive={true} />
      } />
    </Routes>
  );
}

function AppContent() {
  return (
    <Router>
      <RoutesWithProjectKey />
    </Router>
  );
}

function App() {
  return (
    <ProjectProvider>
      <AppContent />
    </ProjectProvider>
  );
}

export default App;
